import React, { useState, useRef, useEffect, useMemo } from "react";
import {
    Box,
    Container,
    Typography,
    useTheme,
    useMediaQuery,
    Grid,
    Tooltip,
    Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchBox from "../../components/common/CustomSearchBar";
import CustomHeader from "../../components/common/CustomHeader";
import CustomInput from "../../components/common/CustomInput";
import CustomButton from "../../components/common/CustomButton";
import CustomCard from "../../components/common/CustomCard";
import CustomTable from "../../components/common/CustomTable";
import CustomSnackbar from "../../components/common/CustomSnackbar";
import SkeletonLoader from "../../components/common/SkeletonLoader";
import { subscriptionApi } from "../../api/subscriptionApi";
import { CircularProgress } from "@mui/material";
import subscriptionPlanSchema from "../../components/validator/SuperAdmin/SubscriptionPlanSettingValidator";


const SubscriptionPlanSetting = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const effectCalled = useRef(false);

    // UI State
    const [showForm, setShowForm] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

    // Form State
    const [formData, setFormData] = useState({
        plan_name: "",
        price: "",
        credit: "",
        allowed_recruiter_count: "",
        plan_expired_in_days: "",
    });

    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [errors, setErrors] = useState({});
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });

    // Show Snackbar
    const showSnackbar = (message, severity = "info") => {
        setSnackbar({
            open: true,
            message,
            severity,
        });
    };

    // Close Snackbar
    const closeSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // Fetch Plans on Mount
    useEffect(() => {
        if (effectCalled.current) return;
        effectCalled.current = true;

        fetchPlans();
    }, []);

    const fetchPlans = async () => {
        try {
            setLoading(true);
            const response = await subscriptionApi.getPlans({ role: "superadmin" });

            if (response?.status === 200 && Array.isArray(response.data)) {
                setPlans(response.data);
            } else {
                showSnackbar(
                    response?.message || "Failed to fetch subscription plans",
                    "error"
                );
                setPlans([]);
            }
        } catch (error) {
            console.error("Error fetching plans:", error);
            showSnackbar("Error fetching subscription plans", "error");
            setPlans([]);
        } finally {
            setLoading(false);
        }
    };

    // Filter plans based on search query
    const filteredPlans = useMemo(() => {
        if (!searchQuery.trim()) return plans;
        const query = searchQuery.toLowerCase();
        return plans.filter(
            (plan) =>
                plan.plan_name?.toLowerCase().includes(query) ||
                plan.price?.toString().includes(query)
        );
    }, [plans, searchQuery]);

    // Sort handler
    const handleSort = (key) => {
        setSortConfig((prev) =>
            prev.key === key
                ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
                : { key, direction: "asc" }
        );
    };

    // Sorted plans derived from filteredPlans
    const sortedPlans = useMemo(() => {
        if (!sortConfig.key) return filteredPlans;
        return [...filteredPlans].sort((a, b) => {
            const aVal = a[sortConfig.key] ?? "";
            const bVal = b[sortConfig.key] ?? "";
            if (typeof aVal === "number" && typeof bVal === "number") {
                return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
            }
            return sortConfig.direction === "asc"
                ? String(aVal).localeCompare(String(bVal))
                : String(bVal).localeCompare(String(aVal));
        });
    }, [filteredPlans, sortConfig]);

    // Handle Input Change
    const handleInputChange = (e) => {
        let { name, value } = e.target;
        const updatedFormData = { ...formData, [name]: value };
        setFormData(updatedFormData);

        if (String(value).trim() === "") {
            setErrors((prev) => ({ ...prev, [name]: "" }));
            return;
        }

        try {
            const fieldVal = name === "plan_name" ? value : (value === "" ? null : Number(value));
            subscriptionPlanSchema.validateSyncAt(name, { [name]: fieldVal });
            setErrors((prev) => ({ ...prev, [name]: "" }));
        } catch (error) {
            setErrors((prev) => ({ ...prev, [name]: error.message }));
        }
    };

    // Validate Form
    const validateForm = (dataToValidate = formData) => {
        const trimmedPlanName = dataToValidate.plan_name?.trim()?.replace(/\s+/g, " ");
        const cleanedData = {
            plan_name: trimmedPlanName,
            price: dataToValidate.price === "" ? null : Number(dataToValidate.price),
            credit: dataToValidate.credit === "" ? null : Number(dataToValidate.credit),
            allowed_recruiter_count: dataToValidate.allowed_recruiter_count === "" ? null : Number(dataToValidate.allowed_recruiter_count),
            plan_expired_in_days: (dataToValidate.plan_expired_in_days === "" || dataToValidate.plan_expired_in_days === null || dataToValidate.plan_expired_in_days === undefined) ? null : Number(dataToValidate.plan_expired_in_days),
        };

        try {
            subscriptionPlanSchema.validateSync(cleanedData, { abortEarly: false });
            setErrors({});
            return true;
        } catch (error) {
            const validationErrors = {};
            if (error.inner) {
                error.inner.forEach((err) => {
                    validationErrors[err.path] = err.message;
                });
            } else {
                validationErrors[error.path] = error.message;
            }
            setErrors(validationErrors);
            showSnackbar("Please correct the errors in the form.", "warning");
            return false;
        }
    };

    // Handle Submit
    const handleSubmit = async (e) => {
        e.preventDefault();

        const trimmedPlanName = formData.plan_name?.trim()?.replace(/\s+/g, " ");
        const normalizedFormData = {
            ...formData,
            plan_name: trimmedPlanName,
        };
        setFormData(normalizedFormData);

        if (!validateForm(normalizedFormData)) return;

        const payload = {
            plan_name: trimmedPlanName,
            price: formData.price === "" ? 0 : Number(formData.price),
            credit: formData.credit === "" ? 0 : Number(formData.credit),
            allowed_recruiter_count: formData.allowed_recruiter_count === "" ? 0 : Number(formData.allowed_recruiter_count),
            plan_expired_in_days: (formData.plan_expired_in_days === "" || formData.plan_expired_in_days === null || formData.plan_expired_in_days === undefined) ? null : Number(formData.plan_expired_in_days),
        };

        try {
            setSubmitting(true);

            if (editingId) {
                // Update existing plan
                const response = await subscriptionApi.updatePlan(editingId, payload);
                if (response?.status === 200) {
                    showSnackbar("Subscription plan updated successfully", "success");
                    setEditingId(null);
                    setShowForm(false);
                    await fetchPlans();
                } else {
                    showSnackbar(
                        response?.message || "Failed to update subscription plan",
                        "error"
                    );
                }
            } else {
                // Create new plan
                const response = await subscriptionApi.createPlan(payload);
                if (response?.status === 201 || response?.status === 200) {
                    showSnackbar("Subscription plan created successfully", "success");
                    setShowForm(false);
                    await fetchPlans();
                } else {
                    showSnackbar(
                        response?.message || "Failed to create subscription plan",
                        "error"
                    );
                }
            }

            // Reset form
            setFormData({
                plan_name: "",
                price: "",
                credit: "",
                allowed_recruiter_count: "",
                plan_expired_in_days: "",
            });
            setErrors({});
        } catch (error) {
            console.error("Error submitting form:", error);
            showSnackbar("Error submitting subscription plan", "error");
        } finally {
            setSubmitting(false);
        }
    };

    // Handle Edit
    const handleEdit = (plan) => {
        setFormData({
            plan_name: plan.plan_name || "",
            price: plan.price !== undefined && plan.price !== null ? String(plan.price) : "",
            credit: plan.credit !== undefined && plan.credit !== null ? String(plan.credit) : "",
            allowed_recruiter_count: plan.allowed_recruiter_count !== undefined && plan.allowed_recruiter_count !== null ? String(plan.allowed_recruiter_count) : "",
            plan_expired_in_days: plan.plan_expired_in_days !== undefined && plan.plan_expired_in_days !== null ? String(plan.plan_expired_in_days) : "",
        });
        setErrors({});
        setEditingId(plan.id);
        setShowForm(true);
    };

    // Handle Add New Plan
    const handleAddNewPlan = () => {
        setEditingId(null);
        setFormData({
            plan_name: "",
            price: "",
            credit: "",
            allowed_recruiter_count: "",
            plan_expired_in_days: "",
        });
        setErrors({});
        setShowForm(true);
    };

    // Handle Cancel
    const handleCancel = () => {
        setShowForm(false);
        setEditingId(null);
        setFormData({
            plan_name: "",
            price: "",
            credit: "",
            allowed_recruiter_count: "",
            plan_expired_in_days: "",
        });
        setErrors({});
    };

    // Table columns definition
    const tableColumns = [
        {
            label: "Plan Name",
            field: "plan_name",
            sortable: true,
            stickyFirst: true,
            headerAlign: "center",
            render: (value) => <Typography sx={{ fontWeight: 600, color: "inherit", textAlign: "center" }}>{value}</Typography>,
        },
        {
            label: "Price (₹)",
            field: "price",
            sortable: true,
            align: "center",
            render: (value) => (
                <Typography sx={{ color: theme.palette.success.main, fontWeight: 600, textAlign: "center" }}>
                    ₹{Number(value).toFixed(2)}
                </Typography>
            ),
        },
        {
            label: "Credits",
            field: "credit",
            sortable: true,
            align: "center",
            render: (value) => <Typography sx={{ textAlign: "center", color: "inherit" }}>{value ?? "—"}</Typography>,
        },
        {
            label: "Allowed Recruiters",
            field: "allowed_recruiter_count",
            sortable: true,
            align: "center",
            render: (value) => <Typography sx={{ textAlign: "center", color: "inherit" }}>{value ?? "—"}</Typography>,
        },
        {
            label: "Plan End in Days",
            field: "plan_expired_in_days",
            sortable: true,
            align: "center",
            render: (value) => (
                <Typography sx={{ textAlign: "center", color: "inherit" }}>
                    {value === null || value === undefined || value === "" ? "—" : value}
                </Typography>
            ),
        },
        {
            label: "Action",
            field: "id",
            sortable: false,
            stickyLast: true,
            align: "center",
            render: (value, row) => (
                <CustomButton
                    size="small"
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => handleEdit(row)}
                    sx={{
                        textTransform: "none",
                        fontSize: "0.8rem",
                    }}
                >
                    Edit
                </CustomButton>
            ),
        },
    ];


    return (
        <Box sx={{ minHeight: "100vh" }}>
            <CustomHeader title="Subscription Plans" />

            <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
                {showForm ? (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            pt: 2,
                            mb: 3
                        }}
                    >
                        <form
                            onSubmit={handleSubmit}
                            style={{
                                width: "100%",
                                maxWidth: 900
                            }}
                        >
                            <CustomCard
                                sx={{
                                    p: 1,
                                    borderRadius: 3,
                                    backgroundColor: theme.palette.background.paper,
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                }}
                            >
                                {/* Header */}
                                <Box sx={{ textAlign: "center", mb: 3 }}>
                                    <Typography
                                        variant="h5"
                                        align="center"
                                        sx={{
                                            fontWeight: 700,
                                            color: theme.palette.primary.main,
                                            pb: 0.3,
                                        }}>
                                        {editingId ? "Edit Subscription Plan" : "Create Subscription Plan"}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: "text.secondary",
                                            textAlign: "center",
                                            mb: 1,
                                            fontSize: "0.85rem",
                                        }}>
                                        {editingId
                                            ? "Update your subscription plan details"
                                            : "Add a new subscription tier"}
                                    </Typography>
                                    <Divider />

                                </Box>

                                {/* FORM */}
                                <Box
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
                                        gap: 2,
                                        overflowY: "auto",
                                        maxHeight: "45vh", // Reduced height to match the smaller width
                                        p: 1, // small padding to separate from scrollbar
                                    }}
                                >
                                    {/* Plan Name */}
                                    <Box>
                                        <CustomInput
                                            label="Plan Name"
                                            tooltip="Enter the name of the subscription plan"
                                            size="small"
                                            name="plan_name"
                                            placeholder="e.g. Free Trial"
                                            value={formData.plan_name}
                                            onChange={handleInputChange}
                                            required
                                            error={!!errors.plan_name}
                                            helperText={errors.plan_name}
                                        />
                                    </Box>

                                    {/* Price */}
                                    <Box>
                                        <CustomInput
                                            label="Price (₹)"
                                            tooltip="Set the price of the plan"
                                            name="price"
                                            size="small"
                                            type="number"
                                            placeholder="e.g. 500"
                                            value={formData.price}
                                            onChange={handleInputChange}
                                            required
                                            error={!!errors.price}
                                            helperText={errors.price}
                                        />
                                    </Box>

                                    {/* Credits */}
                                    <Box>
                                        <CustomInput
                                            label="Credits"
                                            name="credit"
                                            size="small"
                                            tooltip="Number of credits allowed under this plan"
                                            type="number"
                                            placeholder="e.g. 100"
                                            value={formData.credit}
                                            onChange={handleInputChange}
                                            required
                                            error={!!errors.credit}
                                            helperText={errors.credit}
                                        />
                                    </Box>

                                    {/* Allowed Recruiters */}
                                    <Box>
                                        <CustomInput
                                            label="Allowed Recruiters"
                                            tooltip="Number of recruiters allowed under this plan"
                                            name="allowed_recruiter_count"
                                            size="small"
                                            type="number"
                                            placeholder="e.g. 5"
                                            value={formData.allowed_recruiter_count}
                                            onChange={handleInputChange}
                                            required
                                            error={!!errors.allowed_recruiter_count}
                                            helperText={errors.allowed_recruiter_count}
                                        />
                                    </Box>

                                    {/* Plan End In Days */}
                                    <Box>
                                        <CustomInput
                                            label="Plan End in Days"
                                            tooltip="Number of days the plan is valid for"
                                            name="plan_expired_in_days"
                                            type="number"
                                            size="small"
                                            placeholder="e.g. 30"
                                            value={formData.plan_expired_in_days}
                                            onChange={handleInputChange}
                                            error={!!errors.plan_expired_in_days}
                                            helperText={errors.plan_expired_in_days}
                                        />
                                    </Box>
                                </Box>

                                {/* Buttons */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        gap: 2,
                                        pt: 2,
                                        mt: 2,
                                        borderTop: `1px solid ${theme.palette.divider}`,
                                    }}
                                >
                                    <CustomButton
                                        fullWidth
                                        onClick={handleCancel}
                                        sx={{
                                            mt: 1,
                                        }}
                                    >
                                        Cancel
                                    </CustomButton>

                                    <CustomButton
                                        type="submit"
                                        variant="contained"
                                        fullWidth
                                        disabled={
                                            submitting ||
                                            !String(formData.plan_name).trim() ||
                                            String(formData.price).trim() === "" ||
                                            String(formData.credit).trim() === "" ||
                                            String(formData.allowed_recruiter_count).trim() === "" ||
                                            Object.values(errors).some((v) => v)
                                        }
                                        startIcon={
                                            submitting ? <CircularProgress size={22} color="inherit" /> : null
                                        }
                                        sx={{
                                            mt: 1,
                                        }}
                                    >
                                        {submitting
                                            ? editingId
                                                ? "Updating..."
                                                : "Creating..."
                                            : editingId
                                                ? "Update Plan"
                                                : "Create Plan"}
                                    </CustomButton>
                                </Box>
                            </CustomCard>
                        </form>
                    </Box>
                ) : (
                    <Box>
                        <Box
                            sx={{
                                display: "flex",
                                gap: 2,
                                mb: 3,
                                flexDirection: isMobile ? "column" : "row",
                            }}
                        >
                            <SearchBox
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onClear={() => setSearchQuery("")}
                            />

                            <CustomButton onClick={handleAddNewPlan}>
                                Add New Plan
                            </CustomButton>
                        </Box>

                        {loading ? (
                            <SkeletonLoader
                                type="table"
                                rows={5}
                                columns={tableColumns.map(c => c.label)}
                            />
                        ) : (
                            <CustomTable
                                columns={tableColumns}
                                rows={sortedPlans}
                                sortConfig={sortConfig}
                                onSort={handleSort}
                            />
                        )}
                    </Box>
                )}

            </Container>
            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={closeSnackbar}
            />
        </Box>
    );
};

export default SubscriptionPlanSetting;
